// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Ramp control points by hand (M13-24, 11 §3.1).
//
// A ramp or a habitrail is a Catmull-Rom spline through the author's control points, and on the
// 2-D canvas those points are dragged with the pointer (`LayoutCanvas`, M9-04). On the bench
// they are **taken by hand**: a fingertip pinched on one of the selected ramp's points takes the
// point, the point rides the fingertip — in all three dimensions, because a ramp's height is
// the thing a hand can say that a mouse cannot — and it is let go where the pinch opens.
//
// **The draft owns the edit; this owns the hand**, exactly as `partCarry.ts` says of a part.
// Every write goes through the Builder's own `withPoint` under `editLayer`, so a point moved
// here is clamped as the canvas clamps it, tidy to the canvas's tenth of a millimetre, and its
// x and y are snapped by the same `snapPoint` — a ramp's mouth lands on the millimetre and can
// be lined up with the lane it feeds. The gesture is the canvas's own name for it,
// `${layerId}:point${n}`, so a run of frames is one undo; **the gesture is closed at the
// release** (`endGesture`), so a second pinch on the same point is a second undo, which the
// canvas — where the gesture stays open until another edit — does not quite manage.
//
// **A ramp is chosen by touching it.** The canvas selects a layer from a list; a bench has no
// list, and a ramp is a thing in the air that a hand can reach. A pinch within `CURVE_CATCH`
// of a ramp's curve — the simulation's own `Path`, sampled as the ramp is drawn — selects the
// ramp and nothing else; its points then show as handles. The order at a pinch is the canvas's:
// the selected ramp's points first, then a part's handle (the carry's business, untouched
// here), then a ramp's curve, and a pinch on empty playfield puts the ramp down as it puts a
// part down.
//
// **A move the validator refuses is not made** — the point stops where the last good frame
// left it, so a ramp lifted into the glass stops at the glass — for the reason `partCarry.ts`
// gives: the headset's seam rebuilds the session on what is adopted, and the loader throws on
// a machine the validator rejects. The refusal is reported once, until its sentence changes.
//
// **A point is added by pinching the chosen ramp's curve, and taken off by carrying it past the
// playfield's edge** (the M13-24 follow-up). A pinch on the selected ramp's curve that is not on
// one of its points puts a new point there — on the curve, sampled as the simulation samples it,
// so the shape barely moves — and the new point is already in the hand. A point carried until
// the fingertip is past the edge of the playfield stops where it was, fades, and is told "let go
// here to take the point off"; opened there, it comes off — the Builder's own `removePoint`,
// refused at two as the canvas refuses it. Both are the same gesture as the move they begin or
// end, so an added-and-dragged point is one undo and a moved-then-removed point is one undo.
//
// Pure: fingertips in the playfield's frame in, the next draft and what to draw out. No Three.

import { Path } from '../../../core/ts/sim/Path'
import { validateMachine, type MachineLayer, type Point2, type Point3 } from '../../../core/ts/machine'
import { anchorAt, devicesOf, editLayer, endGesture, layersOf, selectLayer, type LayoutDraft } from '../../builder/layoutDraft'
import { snapPoint } from '../../builder/snapping'
import { PROFILE_SAMPLES_PER_SEGMENT, insertAt, isSplineLayer, removePoint, withPoint, type SplineLayer } from '../../builder/splines'
import { BENCH_EN, benchText, type BenchStrings } from './benchStrings'
import { BOARD_MARGIN, CATCH_RADIUS, HANDLE_CATCH, overBoard, type CarryBoard, type CarryHand } from './partCarry'

/** How near a fingertip must be to a control point to take it, room metres: a part's handle's catch. */
export const POINT_CATCH = HANDLE_CATCH
/** How near a fingertip must be to a ramp's curve to choose the ramp, room metres. A ramp is wider than a handle. */
export const CURVE_CATCH = 0.025
/** A handle's drawn radius, room metres: half a sixteen-millimetre target, so a fingertip covers it. */
export const HANDLE_RADIUS = 0.008

/** One control point of the selected ramp, as the picture draws it. */
export interface PointHandle {
  readonly index: number
  /** Playfield metres: x, y, and the point's height over the surface. */
  readonly at: Point3
  /** The hand over it — the one a pinch would take — or nothing. */
  readonly hover: 'left' | 'right' | undefined
  /** In hand. */
  readonly held: boolean
  /** In hand with the fingertip past the playfield's edge: let go, it comes off. */
  readonly leaving: boolean
}

/** The selected ramp's points and its curve, for the picture. */
export interface PointsView {
  readonly layerId: string
  readonly kind: 'ramp' | 'wire'
  readonly points: readonly PointHandle[]
  /**
   * The curve to draw: the spline as the draft has it now — ahead of the machine's own ramp,
   * which waits for the release — **without any point in hand that is leaving** (the ramp
   * follow-up, 26 Sep 2026): a point carried past the edge is previewed off, so the author sees
   * the ramp they will have if they let go there. A point the ramp cannot lose (two left) stays.
   */
  readonly spline: readonly Point3[]
  /** A point is in hand: the picture draws the curve as it is now. */
  readonly shaping: boolean
  /** Changes when the picture should be rebuilt: the ramp, its points, which is lit, which is held. */
  readonly key: string
}

/** A point taken or let go: which ramp, which point, which hand. */
export interface PointGrip {
  readonly layerId: string
  readonly index: number
  readonly hand: 'left' | 'right'
}

/** What happened this frame, for the host's say lines and toasts. */
export interface ShapeEvents {
  /** A ramp was chosen this frame by a touch on its curve. */
  readonly chose: string | undefined
  /** A point was taken this frame. */
  readonly took: PointGrip | undefined
  /** The point in hand was let go this frame. */
  readonly dropped: PointGrip | undefined
  /** The validator's sentence for a move that was not made, said once until it changes. */
  readonly refused: string | undefined
  /** A point was added this frame by a pinch on the chosen ramp's curve — and taken: `took` names it too. */
  readonly added: PointGrip | undefined
  /** The point in hand went past the playfield's edge this frame: a release there takes it off. */
  readonly leaving: PointGrip | undefined
  /** The point in hand was let go past the edge and taken off this frame — `dropped` names it too. */
  readonly removed: PointGrip | undefined
}

export const NO_SHAPE_EVENTS: ShapeEvents = {
  chose: undefined,
  took: undefined,
  dropped: undefined,
  refused: undefined,
  added: undefined,
  leaving: undefined,
  removed: undefined,
}

export interface ShapeFrame extends ShapeEvents {
  readonly draft: LayoutDraft
  /** A point is in hand after this frame: the gesture whose writes the host adopts at the release. */
  readonly shaping: boolean
  /** Hands this file has spoken for — a point in them, a ramp chosen by their pinch — masked from the carry. */
  readonly over: { readonly left: boolean; readonly right: boolean }
  readonly view: PointsView | undefined
}

export interface ShapeOptions {
  readonly board: CarryBoard
  /** The machine's scale in the room, for the catches. */
  readonly scale: number
  /** Mirror snapping on. The Build page's Symmetry row. */
  readonly symmetry: boolean
  /** The language a refusal is worded in (M13-26). English absent. */
  readonly strings?: BenchStrings
}

/**
 * The curve a ramp will have if the hands let go now: the spline without its leaving points, as
 * many as `removePoint` would take — never below two, the Builder's own floor.
 */
function previewOf(layer: SplineLayer, holds: readonly { readonly index: number; readonly leaving: boolean }[]): readonly Point3[] {
  const leaving = new Set(holds.filter((h) => h.leaving).map((h) => h.index))
  if (leaving.size === 0) return layer.spline
  let kept = layer.spline.length
  const out: Point3[] = []
  layer.spline.forEach((point, index) => {
    if (leaving.has(index) && kept > 2) {
      kept -= 1
      return
    }
    out.push(point)
  })
  return out
}

interface Held extends PointGrip {
  /** The point less the fingertip at the pinch, so the point does not jump to the finger (`Grab.offset`). */
  readonly offset: Point3
  /** The history entry this hold writes under: the canvas's `point<n>`, or `insert<n>` for a point added by this pinch. */
  readonly gesture: string
  /** The fingertip was past the playfield's edge on the last frame it held: a release takes the point off. */
  leaving: boolean
}

/** Past the playfield's edge — outside its footprint by the board's margin, at any height. */
export function pastTheEdge(x: number, y: number, board: CarryBoard): boolean {
  return Math.abs(x) > board.width / 2 + BOARD_MARGIN || Math.abs(y) > board.length / 2 + BOARD_MARGIN
}

/**
 * Where a pinch on a ramp's curve adds a point: the nearest sample that is not a control point's
 * own, as the index the new point takes and the place it takes. Nothing when the curve has no
 * sample between its points (fewer than two points).
 */
export function insertionNear(spline: readonly Point3[], x: number, y: number, z: number): { readonly index: number; readonly at: Point3 } | undefined {
  if (spline.length < 2) return undefined
  const path = new Path(spline as readonly (readonly [number, number, number])[], PROFILE_SAMPLES_PER_SEGMENT)
  let best: { index: number; at: Point3 } | undefined
  let bestGap = Infinity
  for (let i = 0; i < path.xs.length; i++) {
    // A control point's own sample would put a second point on top of the first.
    if (i % PROFILE_SAMPLES_PER_SEGMENT === 0) continue
    const segment = Math.floor(i / PROFILE_SAMPLES_PER_SEGMENT)
    if (segment > spline.length - 2) continue
    const at: Point3 = [path.xs[i] as number, path.ys[i] as number, path.zs[i] as number]
    const dx = at[0] - x
    const dy = at[1] - y
    const dz = at[2] - z
    const gap = dx * dx + dy * dy + dz * dz
    if (gap >= bestGap) continue
    bestGap = gap
    best = { index: segment + 1, at }
  }
  return best
}

/** The selected layer, when it is a ramp or a habitrail. */
export function selectedSpline(draft: LayoutDraft): SplineLayer | undefined {
  const id = draft.selectedLayer
  if (id === undefined) return undefined
  const layer = layersOf(draft).find((l) => l.id === id)
  return layer !== undefined && isSplineLayer(layer) ? layer : undefined
}

/** The nearest control point within `catchMetres` of a fingertip, in three dimensions, or nothing. */
export function pointNear(spline: readonly Point3[], x: number, y: number, z: number, catchMetres: number): number | undefined {
  let best: number | undefined
  let bestGap = catchMetres * catchMetres
  spline.forEach((point, index) => {
    const dx = point[0] - x
    const dy = point[1] - y
    const dz = point[2] - z
    const gap = dx * dx + dy * dy + dz * dz
    if (gap > bestGap) return
    bestGap = gap
    best = index
  })
  return best
}

export class RampShaper {
  /**
   * The point in each hand (the two-hands follow-up): both on the chosen ramp, each its own point.
   * While both hold, their writes share one history entry (`<layerId>:hands`), so a two-handed
   * shaping is one undo; the gesture closes when the last hand lets go.
   */
  private readonly holds: { left: Held | undefined; right: Held | undefined } = { left: undefined, right: undefined }
  /** Whether each hand was pinched last frame — starting closed, deliberately (`CabinetGrab`, `PartCarry`). */
  private readonly wasGrab = { left: true, right: true }
  /** Hands whose pinch this took — a point, a ramp — and which stay this file's until they open. */
  private readonly spoken = { left: false, right: false }
  /** The last refusal said, so a hand held against the glass is told once. */
  private refusedNow: string | undefined
  /** Each ramp's curve as the simulation samples it, kept per layer object: a layer that did not change is not resampled. */
  private readonly curves = new WeakMap<MachineLayer, readonly Point3[]>()

  /** A hand holding a point — the right when both do — or nothing. */
  get holding(): 'left' | 'right' | undefined {
    return this.holds.right?.hand ?? this.holds.left?.hand
  }

  /** Both hands hold a point. */
  get holdingBoth(): boolean {
    return this.holds.left !== undefined && this.holds.right !== undefined
  }

  /** The hold in the other hand, or nothing. */
  private other(side: 'left' | 'right'): Held | undefined {
    return side === 'left' ? this.holds.right : this.holds.left
  }

  /** A second hand has taken a point: from here both hands' writes are one entry, until both let go. */
  private share(layerId: string): string {
    const gesture = `${layerId}:hands`
    if (this.holds.left !== undefined) this.holds.left = { ...this.holds.left, gesture, leaving: this.holds.left.leaving }
    if (this.holds.right !== undefined) this.holds.right = { ...this.holds.right, gesture, leaving: this.holds.right.leaving }
    return gesture
  }

  /** One frame of pinch points in the playfield's frame. `carrying` is true while a part is in a hand, and then nothing here takes. */
  advance(draft: LayoutDraft, hands: readonly CarryHand[], options: ShapeOptions, carrying: boolean): ShapeFrame {
    let next = draft
    let chose: string | undefined
    let took: PointGrip | undefined
    let dropped: PointGrip | undefined
    let refused: string | undefined
    let added: PointGrip | undefined
    let leaving: PointGrip | undefined
    let removed: PointGrip | undefined
    const over = { left: false, right: false }
    const s = options.scale > 0 ? options.scale : 1
    const strings = options.strings ?? BENCH_EN

    const edges: Partial<Record<'left' | 'right', boolean>> = {}
    for (const hand of hands) {
      const grab = hand.present && hand.grab
      edges[hand.handedness] = grab && !this.wasGrab[hand.handedness]
      this.wasGrab[hand.handedness] = hand.present ? hand.grab : true
      if (!grab) this.spoken[hand.handedness] = false
    }

    // Each point in hand, the left's first.
    for (const side of ['left', 'right'] as const) {
      const held = this.holds[side]
      if (held === undefined) continue
      const hand = hands.find((h) => h.handedness === side)
      const layer = layersOf(next).find((l) => l.id === held.layerId)
      if (hand === undefined || !hand.present || !hand.grab || layer === undefined || !isSplineLayer(layer)) {
        // The release: what was written stands — or, past the edge, the point comes off under the
        // same gesture — and when no hand holds any more the gesture closes, so the next pinch is
        // its own undo.
        const grip: PointGrip = { layerId: held.layerId, index: held.index, hand: held.hand }
        if (held.leaving && layer !== undefined && isSplineLayer(layer)) {
          const candidate = editLayer(next, held.layerId, (l) => (l === layer ? removePoint(layer, held.index) : l), held.gesture)
          if (candidate !== next) {
            const first = validateMachine(candidate.definition).errors[0]
            if (first === undefined) {
              next = candidate
              removed = removed ?? grip
              // The other hand's point moves down one if it was after this one.
              const other = this.other(side)
              if (other !== undefined && other.index > held.index) this.holds[side === 'left' ? 'right' : 'left'] = { ...other, index: other.index - 1 }
            } else {
              refused = benchText(strings, 'toast.notPlaced', { message: first.message })
            }
          }
        }
        dropped = dropped ?? grip
        this.holds[side] = undefined
        if (this.holds.left === undefined && this.holds.right === undefined) {
          next = endGesture(next)
          this.refusedNow = undefined
        }
      } else if (pastTheEdge(hand.x, hand.y, options.board)) {
        // Past the edge: the point stays where it was, and a release here takes it off — unless
        // it is one of the last two, which the Builder refuses, said once.
        over[hand.handedness] = true
        if (layer.spline.length > 2) {
          if (!held.leaving) leaving = leaving ?? { layerId: held.layerId, index: held.index, hand: held.hand }
          held.leaving = true
        } else {
          held.leaving = false
          const sentence = benchText(strings, 'toast.twoPoints')
          if (sentence !== this.refusedNow) {
            this.refusedNow = sentence
            refused = sentence
          }
        }
      } else {
        over[hand.handedness] = true
        if (held.leaving) {
          held.leaving = false
          this.refusedNow = undefined
        }
        const target: Point3 = [hand.x + held.offset[0], hand.y + held.offset[1], hand.z + held.offset[2]]
        const snapped = snapPoint([target[0], target[1]], {
          devices: devicesOf(next),
          toleranceMetres: CATCH_RADIUS / s,
          symmetry: options.symmetry,
        })
        // Through the Builder's own `withPoint` — clamped and tidied as the canvas does it — and
        // written only when the point actually moves: the canvas writes on every pointer move,
        // but a bench that wrote a still hand's frame would re-validate and replan thirty times a
        // second for nothing.
        const shaped = withPoint(layer, held.index, [snapped.at[0], snapped.at[1], target[2]])
        const before = layer.spline[held.index]
        const after = shaped.spline[held.index]
        const moved = before === undefined || after === undefined || before[0] !== after[0] || before[1] !== after[1] || before[2] !== after[2]
        const candidate = moved ? editLayer(next, held.layerId, (l) => (l === layer ? shaped : l), held.gesture) : next
        if (candidate !== next) {
          const first = validateMachine(candidate.definition).errors[0]
          if (first === undefined) {
            next = candidate
            this.refusedNow = undefined
          } else {
            const sentence = benchText(strings, 'toast.notPlaced', { message: first.message })
            // Said once per kind of refusal, not once per millimetre (the 1.0.106 round: 146
            // "tops out at 0.09xx m" lines in eleven seconds, one a frame, each a new number).
            const kind = sentence.replace(/[0-9]/g, '#')
            if (kind !== this.refusedNow) {
              this.refusedNow = kind
              refused = sentence
            }
          }
        }
      }
    }

    if (!carrying) {
      for (const hand of hands) {
        if (this.holds[hand.handedness] !== undefined) continue
        if (!hand.present || hand.atPlinth !== undefined) continue
        if (this.spoken[hand.handedness]) {
          over[hand.handedness] = true
          continue
        }
        if (edges[hand.handedness] !== true) continue
        const other = this.other(hand.handedness)
        // The selected ramp's points first, as the canvas has it — and a second hand takes a
        // second point of the same ramp, never the first hand's.
        const selected = selectedSpline(next)
        if (selected !== undefined) {
          const index = pointNear(selected.spline, hand.x, hand.y, hand.z, POINT_CATCH / s)
          const point = index === undefined ? undefined : selected.spline[index]
          if (index !== undefined && point !== undefined && !(other !== undefined && other.index === index)) {
            const gesture = other === undefined ? `${selected.id}:point${index}` : this.share(selected.id)
            this.holds[hand.handedness] = {
              layerId: selected.id,
              index,
              hand: hand.handedness,
              offset: [point[0] - hand.x, point[1] - hand.y, point[2] - hand.z],
              gesture,
              leaving: false,
            }
            this.spoken[hand.handedness] = true
            took = took ?? { layerId: selected.id, index, hand: hand.handedness }
            over[hand.handedness] = true
            continue
          }
        }
        // With a point already in the other hand, the free hand only shapes that ramp: it does
        // not choose another, take a part, or put the ramp down.
        if (other !== undefined) {
          const near = this.curveNear(next, hand.x, hand.y, hand.z, CURVE_CATCH / s)
          if (near === other.layerId && selected !== undefined) {
            // Not on a point: a pinch on the other hand's point is not a new one beside it.
            const onAPoint = pointNear(selected.spline, hand.x, hand.y, hand.z, POINT_CATCH / s) !== undefined
            const place = onAPoint ? undefined : insertionNear(selected.spline, hand.x, hand.y, hand.z)
            if (place !== undefined) {
              const gesture = this.share(selected.id)
              const grown = insertAt(selected, place.index, place.at)
              const candidate = editLayer(next, selected.id, (l) => (l === selected ? grown : l), gesture)
              const first = candidate === next ? undefined : validateMachine(candidate.definition).errors[0]
              if (candidate !== next && first === undefined) {
                const at = grown.spline[place.index] as Point3
                next = candidate
                // The other hand's point moves up one if it was at or after the new one.
                const otherSide = hand.handedness === 'left' ? 'right' : 'left'
                const kept = this.holds[otherSide]
                if (kept !== undefined && kept.index >= place.index) this.holds[otherSide] = { ...kept, index: kept.index + 1 }
                this.holds[hand.handedness] = {
                  layerId: selected.id,
                  index: place.index,
                  hand: hand.handedness,
                  offset: [at[0] - hand.x, at[1] - hand.y, at[2] - hand.z],
                  gesture,
                  leaving: false,
                }
                added = added ?? { layerId: selected.id, index: place.index, hand: hand.handedness }
                took = took ?? added
              } else if (first !== undefined) {
                refused = benchText(strings, 'toast.notPlaced', { message: first.message })
              }
            }
            this.spoken[hand.handedness] = true
            over[hand.handedness] = true
          }
          continue
        }
        const onBoard = overBoard(hand.x, hand.y, hand.z, options.board)
        // A part's handle is the carry's.
        if (onBoard && anchorAt(devicesOf(next), [hand.x, hand.y], HANDLE_CATCH / s) !== undefined) continue
        // A ramp's curve chooses the ramp.
        const near = this.curveNear(next, hand.x, hand.y, hand.z, CURVE_CATCH / s)
        if (near !== undefined) {
          this.spoken[hand.handedness] = true
          over[hand.handedness] = true
          if (near !== next.selectedLayer) {
            next = selectLayer(next, near)
            chose = near
            break
          }
          // The chosen ramp's own curve, away from its points: a point is added there, in hand.
          const layer = selected
          const place = layer === undefined ? undefined : insertionNear(layer.spline, hand.x, hand.y, hand.z)
          if (layer !== undefined && place !== undefined) {
            const gesture = `${layer.id}:insert${place.index}`
            const grown = insertAt(layer, place.index, place.at)
            const candidate = editLayer(next, layer.id, (l) => (l === layer ? grown : l), gesture)
            const first = candidate === next ? undefined : validateMachine(candidate.definition).errors[0]
            if (candidate !== next && first === undefined) {
              const at = grown.spline[place.index] as Point3
              next = candidate
              this.holds[hand.handedness] = {
                layerId: layer.id,
                index: place.index,
                hand: hand.handedness,
                offset: [at[0] - hand.x, at[1] - hand.y, at[2] - hand.z],
                gesture,
                leaving: false,
              }
              added = { layerId: layer.id, index: place.index, hand: hand.handedness }
              took = added
            } else if (first !== undefined) {
              refused = benchText(strings, 'toast.notPlaced', { message: first.message })
            }
          }
          break
        }
        // Empty playfield puts the ramp down, as it puts a part down.
        if (onBoard && next.selectedLayer !== undefined) next = selectLayer(next, undefined)
      }
    }

    return {
      draft: next,
      chose,
      took,
      dropped,
      refused,
      added,
      leaving,
      removed,
      shaping: this.holds.left !== undefined || this.holds.right !== undefined,
      over,
      view: carrying ? undefined : this.viewOf(next, hands, s),
    }
  }

  /**
   * Let go without writing more — the input method changed, the session recycled, the mode is
   * leaving. What was written stands (the canvas's rule: a ramp is its own preview), and the
   * gesture closes.
   */
  cancel(draft: LayoutDraft): LayoutDraft {
    const wasHeld = this.holds.left !== undefined || this.holds.right !== undefined
    this.release()
    return wasHeld ? endGesture(draft) : draft
  }

  /** Every latch let go: the hands were reassigned. */
  release(): void {
    this.holds.left = undefined
    this.holds.right = undefined
    this.refusedNow = undefined
    this.spoken.left = false
    this.spoken.right = false
    this.wasGrab.left = true
    this.wasGrab.right = true
  }

  /** The selected ramp's points, the hand over one, the ones in hand — or nothing with no ramp selected. */
  private viewOf(draft: LayoutDraft, hands: readonly CarryHand[], scale: number): PointsView | undefined {
    const layer = selectedSpline(draft)
    if (layer === undefined) return undefined
    const holds = [this.holds.left, this.holds.right].filter((h): h is Held => h !== undefined && h.layerId === layer.id)
    let hoverIndex: number | undefined
    let hoverHand: 'left' | 'right' | undefined
    for (const hand of hands) {
      if (this.holds[hand.handedness] !== undefined) continue
      if (!hand.present || hand.grab || hand.atPlinth !== undefined) continue
      const index = pointNear(layer.spline, hand.x, hand.y, hand.z, POINT_CATCH / scale)
      if (index === undefined || holds.some((h) => h.index === index)) continue
      hoverIndex = index
      hoverHand = hand.handedness
      break
    }
    const points = layer.spline.map((at, index) => {
      const hold = holds.find((h) => h.index === index)
      return {
        index,
        at,
        hover: index === hoverIndex ? hoverHand : undefined,
        held: hold !== undefined,
        leaving: hold !== undefined && hold.leaving,
      }
    })
    const heldKey = holds.map((h) => `${h.index}${h.leaving ? '!' : ''}`).join(',')
    const key = `${layer.id}|${layer.spline.map((p) => p.join(',')).join(';')}|${hoverIndex ?? '-'}|${heldKey === '' ? '-' : heldKey}`
    return { layerId: layer.id, kind: layer.kind, points, spline: previewOf(layer, holds), shaping: holds.length > 0, key }
  }

  /** The ramp whose curve is nearest a fingertip within `catchMetres`, in three dimensions, or nothing. */
  private curveNear(draft: LayoutDraft, x: number, y: number, z: number, catchMetres: number): string | undefined {
    let best: string | undefined
    let bestGap = catchMetres * catchMetres
    for (const layer of layersOf(draft)) {
      if (!isSplineLayer(layer)) continue
      for (const sample of this.samplesOf(layer)) {
        const dx = sample[0] - x
        const dy = sample[1] - y
        const dz = sample[2] - z
        const gap = dx * dx + dy * dy + dz * dz
        if (gap > bestGap) continue
        bestGap = gap
        best = layer.id
      }
    }
    return best
  }

  /** The curve as the simulation samples it — the ramp the ball rides and the picture draws. */
  private samplesOf(layer: SplineLayer): readonly Point3[] {
    const cached = this.curves.get(layer)
    if (cached !== undefined) return cached
    let samples: Point3[] = []
    if (layer.spline.length >= 2) {
      const path = new Path(layer.spline as readonly (readonly [number, number, number])[], PROFILE_SAMPLES_PER_SEGMENT)
      for (let i = 0; i < path.xs.length; i++) samples.push([path.xs[i] as number, path.ys[i] as number, path.zs[i] as number])
    } else {
      samples = layer.spline.map((p) => [p[0], p[1], p[2]] as Point3)
    }
    this.curves.set(layer, samples)
    return samples
  }
}

/** The apex of a spline — its highest control point — as a place on the playfield with a lift, for a callout. */
export function apexOf(spline: readonly Point3[]): { readonly at: Point2; readonly lift: number } | undefined {
  let best: Point3 | undefined
  for (const point of spline) if (best === undefined || point[2] > best[2]) best = point
  return best === undefined ? undefined : { at: [best[0], best[1]], lift: Math.max(0, best[2]) }
}
